'use client';

import { useState, useEffect } from 'react';

import { GOALS_LIMIT } from '@/constants';
import { useToast } from '@/contexts/ToastContext';
import Button from '@/components/common/Button';
import Text from '@/components/common/Text';
import GoalListRow from '@/components/goals/GoalListRow';
import { GoalModel } from '@/types/goals';
import { getGoals } from '@/repositories/goals';

interface GoalListProps {
  propsSelectedGoal: GoalModel | undefined;
  onSelectGoal: (goal: GoalModel | undefined) => void;
  onMoveToGoals?: () => void;
}

/**
 * 目標一覧
 * @param param0
 * @returns
 */
const GoalList: React.FC<GoalListProps> = ({ propsSelectedGoal, onSelectGoal, onMoveToGoals }) => {
  /**
   * React context
   */
  const { showErrorMessage } = useToast();

  /**
   * useState
   */
  const [goals, setGoals] = useState<GoalModel[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [limit] = useState<number>(GOALS_LIMIT);
  const [offset, setOffset] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * もっとみる押下時の処理
   */
  const handleGetMore = async () => {
    if (!hasMore) return;

    // ボタンローディング表示
    setIsLoading(true);

    try {
      const resGoals = await getGoals(limit, offset);
      if (!resGoals.ok) {
        throw new Error(resGoals.detail);
      }

      // 1秒の遅延を追加(更新中であることを見せる)
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const apiGoals = resGoals.data?.goals;
      if (apiGoals != null) {
        setGoals((prevValue) => [...prevValue, ...apiGoals]);
        setHasMore(resGoals.data?.has_more !== undefined ? resGoals.data?.has_more : false);
        setOffset((prevOffset) => prevOffset + limit);
      }
    } catch {
      showErrorMessage('データ通信に失敗しました。しばらくしてから再度お試しください。');
    }

    // ボタンローディング非表示
    setIsLoading(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const resGoals = await getGoals(limit, offset);
        if (!resGoals.ok) {
          throw new Error(resGoals.detail);
        }

        const apiGoals = resGoals.data?.goals;
        if (apiGoals != null) {
          setGoals(apiGoals);
          setHasMore(resGoals.data?.has_more !== undefined ? resGoals.data?.has_more : false);
          setOffset(GOALS_LIMIT);
        }
      } catch {
        showErrorMessage('データ通信に失敗しました。しばらくしてから再度お試しください。');
      }
    };

    fetchData().catch((error) => {
      console.error('An error occurred in fetchData:', error);
      showErrorMessage('予期せぬエラーが発生しました。');
    });
  }, []);

  return (
    <>
      {goals.length > 0 ? (
        <>
          <ul className="space-y-4">
            <li
              key={0}
              className="w-full h-24 bg-cover bg-center rounded-lg bg-white border border-dashed border-gray-400">
              <div className="flex">
                <div className="grow p-4 whitespace-pre-line break-all">
                  <Text className="italic text-gray-500">目標設定なし</Text>
                  <br />
                  <Text className="italic text-xs text-gray-500">（試合観戦ノートなど、目標設定が不要な場合）</Text>
                </div>
                <div className="pt-2">
                  {!propsSelectedGoal ? (
                    <div className="flex justify-center items-center text-white bg-green-500 min-w-[40px] min-h-[40px] rounded-full mr-2 pr-0 pl-0">
                      <svg
                        className="w-[18px] h-[18px]"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="none"
                        viewBox="0 0 24 24">
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 11.917 9.724 16.5 19 7.5"
                        />
                      </svg>
                    </div>
                  ) : (
                    <Button
                      className="min-w-[40px] min-h-[40px] mr-2 pr-0 pl-0"
                      type="button"
                      buttonType="outlinePrimary"
                      isLoading={isLoading}
                      onClick={() => onSelectGoal(undefined)}>
                      <svg
                        className="w-[18px] h-[18px]"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="none"
                        viewBox="0 0 24 24">
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 11.917 9.724 16.5 19 7.5"
                        />
                      </svg>
                    </Button>
                  )}
                </div>
              </div>
            </li>
            {goals?.map((goal) => (
              <GoalListRow
                goal={goal}
                isSelectedGoal={propsSelectedGoal?.id === goal.id}
                key={goal.id}
                onSelectGoal={onSelectGoal}
              />
            ))}
            {hasMore && (
              <li className="pb-4 ">
                <Button
                  type="button"
                  buttonType="outlinePrimary"
                  className="w-full"
                  isLoading={isLoading}
                  loadingMessage="Loading..."
                  onClick={handleGetMore}>
                  もっとみる
                </Button>
              </li>
            )}
          </ul>
        </>
      ) : (
        <div className="bg-gray-100 rounded text-base text-gray-500 p-4">
          <div className="italic">目標・課題が設定されていません</div>
          <div className="">
            <span className="text-sm text-gray-500 hover:text-blue-500 cursor-pointer" onClick={onMoveToGoals}>
              → My Goals設定へ
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default GoalList;
